import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Grid,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import spiritualImg from "../assets/image.jpg";

const navItems = ["HOME", "ABOUT US", "SERVICES", "CATEGORY", "VISION", "CONTACT US"];

export default function SpiritualHero() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg,#5d4037,#3e2723)",
        color: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "url('https://www.transparenttextures.com/patterns/music.png')",
          opacity: 0.07,
          pointerEvents: "none",
        }}
      />

      {/* ================= NAVBAR ================= */}
      <AppBar
        position="static"
        sx={{
          backgroundColor: "#6d4c41",
          boxShadow: "none",
          px: { xs: 1, md: 3 },
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", minHeight: { xs: 64, md: 72 } }}>
          
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, md: 2 } }}>
            <Box
              sx={{
                width: { xs: 38, md: 45 },
                height: { xs: 38, md: 45 },
                borderRadius: "50%",
                border: "2px solid #ffca28",
                backgroundColor: "#b71c1c",
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography
                sx={{
                  color: "#ffca28",
                  fontWeight: 700,
                  fontSize: { xs: 13, sm: 15, md: 16 },
                  lineHeight: 1.3,
                }}
              >
                संगीत विद्यालय शिवाजी
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#ddd",
                  fontSize: { xs: 10, md: 11 },
                  display: { xs: "none", sm: "block" },
                }}
              >
                Your Spiritual Music Journey
              </Typography>
            </Box>
          </Box>

          {/* Desktop nav */}
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 3 }}>
            {navItems.map((item) => (
              <Typography
                key={item}
                sx={{
                  fontSize: 13,
                  cursor: "pointer",
                  letterSpacing: "0.5px",
                  fontWeight: 500,
                  transition: "color 0.2s",
                  "&:hover": { color: "#ffca28" },
                }}
              >
                {item}
              </Typography>
            ))}
            <SearchIcon sx={{ cursor: "pointer", "&:hover": { color: "#ffca28" } }} />
          </Box>

          {/* Mobile: search + hamburger */}
          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 0.5 }}>
            <IconButton color="inherit" size="small">
              <SearchIcon />
            </IconButton>
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Mobile Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 260,
            background: "linear-gradient(180deg, #5d4037 0%, #3e2723 100%)",
            color: "#fff",
          },
        }}
      >
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ color: "#ffca28", fontWeight: 700, fontSize: 15 }}>
            Menu
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: "rgba(255,202,40,0.2)" }} />
        <List>
          {navItems.map((item) => (
            <ListItem
              key={item}
              button
              onClick={() => setDrawerOpen(false)}
              sx={{
                py: 1.5,
                "&:hover": { backgroundColor: "rgba(255,202,40,0.08)" },
              }}
            >
              <ListItemText
                primary={item}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  color: "#fff",
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* ================= HERO SECTION ================= */}
      <Container
        maxWidth="xl"
        disableGutters
        sx={{
          position: "relative",
          zIndex: 1,
          py: { xs: 6, sm: 8, md: 10 },
          px: { xs: 3, sm: 4, md: 8 },
        }}
      >
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">

          {/* LEFT SIDE CONTENT */}
          <Grid item xs={12} md={6}>
            {/* Badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: "#d32f2f",
                px: { xs: 2, md: 3 },
                py: { xs: 0.6, md: 1 },
                borderRadius: "30px",
                mb: { xs: 2, md: 3 },
              }}
            >
              <MusicNoteIcon sx={{ mr: 1, fontSize: { xs: 18, md: 22 } }} />
              <Typography sx={{ fontSize: { xs: 13, md: 15 } }}>
                संगीत विद्यालय शिवाजी
              </Typography>
            </Box>

            {/* Heading */}
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: 34, sm: 44, md: 58, lg: 64 },
                fontWeight: 800,
                color: "#ffca28",
                lineHeight: 1.1,
                letterSpacing: { xs: "-0.5px", md: "-1px" },
              }}
            >
              Spiritual म्युझिक जर्नी
            </Typography>

            {/* Subheading */}
            <Typography
              sx={{
                mt: { xs: 1.5, md: 2 },
                fontSize: { xs: 17, sm: 22, md: 26, lg: 30 },
                color: "#e0e0e0",
                lineHeight: 1.35,
              }}
            >
              Your spiritual music journey begins here
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                mt: { xs: 2, md: 3 },
                color: "#ddd",
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.75,
                maxWidth: { md: "480px" },
              }}
            >
              आपल्या आध्यात्मिक संगीत प्रवासाची सुरुवात येथे करा।
              मराठी, हिंदी आणि कन्नड भजन, आरती आणि भक्तिगीतांचा अनमोल संग्रह।
            </Typography>

            {/* CTA Buttons */}
            <Box
              sx={{
                mt: { xs: 4, md: 5 },
                display: "flex",
                gap: { xs: 2, md: 3 },
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                sx={{
                  backgroundColor: "#ffca28",
                  color: "#000",
                  fontWeight: 700,
                  px: { xs: 3, md: 4 },
                  py: { xs: 1.2, md: 1.5 },
                  borderRadius: "30px",
                  fontSize: { xs: 13, md: 14 },
                  "&:hover": { backgroundColor: "#ffc107" },
                }}
              >
                EXPLORE MUSIC
              </Button>

              <Button
                variant="outlined"
                startIcon={<HeadphonesIcon />}
                sx={{
                  borderColor: "#ffca28",
                  color: "#ffca28",
                  fontWeight: 600,
                  px: { xs: 3, md: 4 },
                  py: { xs: 1.2, md: 1.5 },
                  borderRadius: "30px",
                  fontSize: { xs: 13, md: 14 },
                  "&:hover": {
                    backgroundColor: "rgba(255,202,40,0.1)",
                    borderColor: "#ffca28",
                  },
                }}
              >
                LISTEN NOW
              </Button>
            </Box>
          </Grid>

          {/* RIGHT SIDE IMAGE */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              mt: { xs: 2, md: 0 },
            }}
          >
            <Box
              component="img"
              src={spiritualImg}
              alt="Spiritual"
              sx={{
                width: { xs: 200, sm: 260, md: 320, lg: 360 },
                maxWidth: "90%",
                borderRadius: "50%",
                border: { xs: "6px solid #ffca28", md: "10px solid #ffca28" },
                outline: { xs: "3px solid #fff", md: "4px solid #fff" },
                boxShadow: "0 25px 60px rgba(0,0,0,0.6)",
                display: "block",
              }}
            />
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
}